import { Select } from '../../../shared/components'
import { useAuthStore } from '../../auth/store'
import { useBookingServices } from '../../booking-services/hooks/useBookingServices'
import type { ProductSaleMode } from '../types'

interface SaleModeSectionProps {
  saleMode: ProductSaleMode
  bookingServiceId: string
  onChange: (patch: {
    saleMode: ProductSaleMode
    bookingServiceId: string
  }) => void
}

const SALE_MODE_OPTIONS = [
  { title: 'Price', value: 'PRICE' },
  { title: 'Requires consultation', value: 'CONSULTATION_REQUIRED' },
]

/**
 * PE-2099 — sale mode toggle (Price vs Requires Consultation).
 *
 * PRICE products are bought at `basePrice`. CONSULTATION_REQUIRED products carry
 * no purchasable price and instead link to one of the merchant's bookable
 * services (PE-2100), so the storefront offers a consultation booking rather
 * than an add-to-cart. Mirrors rack-api's CreateProductDto, where `basePrice` is
 * `@ValidateIf(saleMode === PRICE)` and `bookingServiceId` is
 * `@ValidateIf(saleMode === CONSULTATION_REQUIRED)`.
 *
 * The booking-service list is scoped server-side by `x-tenant-id`, so it only
 * loads once an admin has a merchant selected.
 */
export const SaleModeSection = ({ saleMode, bookingServiceId, onChange }: SaleModeSectionProps) => {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId)
  const { data: services = [], isLoading, error } = useBookingServices(
    saleMode === 'CONSULTATION_REQUIRED' ? selectedTenantId : null
  )

  const isConsultation = saleMode === 'CONSULTATION_REQUIRED'
  const activeServices = services.filter((s) => s.isActive)

  return (
    <div className="grid grid-cols-3 gap-8">
      <Select
        label="Sale mode"
        options={SALE_MODE_OPTIONS}
        value={saleMode}
        className="h-[38px]"
        placeholder="Select sale mode"
        onChange={(value) =>
          onChange({
            saleMode: value as ProductSaleMode,
            // Switching back to Price drops the consultation link so the payload
            // never carries a stale bookingServiceId.
            bookingServiceId: value === 'CONSULTATION_REQUIRED' ? bookingServiceId : '',
          })
        }
      />

      {isConsultation && (
        <div className="col-span-2">
          <Select
            label="Consultation service"
            options={activeServices.map((s) => ({ title: s.title, value: s.id }))}
            value={bookingServiceId}
            className="h-[38px]"
            placeholder={isLoading ? 'Loading services…' : 'Select a bookable service'}
            disabled={isLoading || activeServices.length === 0}
            onChange={(value) => onChange({ saleMode, bookingServiceId: value })}
          />
          {error ? (
            <p className="text-xs text-red-500 mt-1">Could not load booking services.</p>
          ) : !isLoading && activeServices.length === 0 ? (
            <p className="text-xs text-red-500 mt-1">
              This merchant has no active booking services yet — create one under Booking Services first.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              Customers book this service instead of buying the product at a price.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
